import { useState, useEffect, useCallback } from "react";
import { CalendarCheck, X } from "lucide-react";
import { CALENDLY_URL } from "@/lib/site";

const SESSION_KEY = "lr-sticky-dismissed";

export const StickyCta = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === "1") setDismissed(true);
  }, []);

  useEffect(() => {
    const onMenu = (e) => setMenuOpen(Boolean(e.detail?.open));
    window.addEventListener("lr-menu", onMenu);
    return () => window.removeEventListener("lr-menu", onMenu);
  }, []);

  const evaluate = useCallback(() => {
    const hero = document.querySelector('[data-testid="hero-section"]');
    const footer = document.querySelector("footer");
    const y = window.scrollY;
    const pastHero = hero ? y > hero.offsetTop + hero.offsetHeight - 120 : y > 600;
    const footerInView = footer
      ? footer.getBoundingClientRect().top < window.innerHeight - 40
      : false;
    setVisible(pastHero && !footerInView);
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          evaluate();
          ticking = false;
        });
      }
    };
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [evaluate]);

  const handleClose = () => {
    setDismissed(true);
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  const show = visible && !dismissed && !menuOpen;

  return (
    <div
      data-testid="sticky-cta-bar"
      aria-hidden={!show}
      className={`fixed inset-x-0 bottom-0 z-40 transition-all duration-500 ${
        show ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
      }`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="bg-legacy border-t border-gold/60 shadow-[0_-12px_40px_-20px_rgba(17,17,17,0.6)]">
        <div className="container-lr py-3 md:py-3.5 flex items-center justify-between gap-4">
          <p className="hidden md:block text-ivory text-[0.95rem] leading-snug">
            Ready to explore whether Infinite Banking fits your goals?
          </p>
          <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sticky-cta-button"
              className="btn-base btn-gold flex-1 md:flex-none text-sm px-5 py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ivory"
            >
              <CalendarCheck size={17} />
              Book Your Free Strategy Call
            </a>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Dismiss strategy call bar"
              data-testid="sticky-cta-close"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-taupe hover:text-ivory hover:bg-white/10 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
